import {
  type ApiError,
  useForgetModuleGrips,
  useInitializeModule,
  useMe,
  useResetModuleRecoveryLatch,
  useRunModuleDiagnostics,
} from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'
import { Fragment, type ReactNode } from 'react'
import { errorMessage, t } from '@ctcells/i18n'

import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { useScreenAccess } from '../screenAccess.ts'

import { ModuleInitializeCard } from './ModuleInitializeCard.tsx'
import { ModulePrepareContainer } from './ModulePrepareContainer.tsx'
import { moduleControlStatus } from './moduleControlStatus.ts'
import {
  ControlButton,
  DiagnosticsResult,
  ModuleStatusChips,
  ReasonActionButton,
  ResultAccepted,
  ResultFailure,
} from './ModuleControlRows.tsx'
import {
  DIAGNOSTICS_OPERATIONS,
  FORGET_GRIPS_OPERATIONS,
  initializeGate,
  moduleWriteGate,
  occupiedPointLabel,
  occupiedPointsOf,
  RESET_RECOVERY_OPERATIONS,
} from './moduleInitializeGate.ts'

/**
 * 모듈 「초기화」 컨테이너 — 모듈 서랍(`ModuleDetailLive`)이 **live 경로에서만** 싣는다.
 *
 * 재료는 전부 서버 값이다:
 *
 * | 축 | 출처 |
 * | --- | --- |
 * | 권한 | `GET /auth/me` `operations`(`initializeModuleControl`) · 모르면 권한 5종 `control` |
 * | 제어락 · stale | `useScreenAccess()` |
 * | 단독 모드 · 허용 명령 · 점유 | 이 모듈의 `ModuleSnapshot`(`GET /modules` + `module_status` 구독 무효화) |
 *
 * ⚠ 「도는 작업」 축은 없다 — 서버가 배치 중에도 초기화를 받는다(v0.9.1 · `moduleInitializeGate.ts` 머리말).
 *
 * 🔴 **목 갈래가 없다.** demo 경로의 서랍은 이 카드를 싣지 않는다 — 보낼 곳이 없는 버튼을 세우지 않는다.
 */
export interface ModuleInitializeContainerProps {
  module: ModuleSnapshot
  moduleName: string
}

export function ModuleInitializeContainer({ module, moduleName }: ModuleInitializeContainerProps) {
  const access = useScreenAccess()
  const { operations } = useMe()
  const initialize = useInitializeModule()
  const forget = useForgetModuleGrips()
  const resetRecovery = useResetModuleRecoveryLatch()
  const diagnostics = useRunModuleDiagnostics()

  const control = initializeGate({
    access,
    operations,
    controlOwner: module.control_owner,
    allowedCommands: module.allowed_commands,
    sending: initialize.isPending,
  })

  /*
   * 🔴 결과는 «이 모듈의» 것만 보인다. 서랍에서 다른 모듈로 옮겨도 컨테이너가 같은 자리에 남으면
   *    앞 모듈의 202·실패가 새 모듈 밑에 선다 — 부르는 쪽이 `key={module_id}` 로 새로 세운다.
   */
  const error = initialize.error ?? undefined

  /** 「제어」 카드의 다른 쓰기 셋 — 「초기화」와 같은 차례의 게이트(`moduleWriteGate`) */
  const writeGate = (operationIds: readonly string[], sending: boolean) =>
    moduleWriteGate({
      access,
      operations,
      operationIds,
      controlOwner: module.control_owner,
      sending,
    })
  /** 한 조작의 결과 줄 — 거절이 먼저, 받았으면 「받았습니다」, 아니면 없다 */
  const outcome = (action: string, caught: ApiError | null, accepted: boolean): ReactNode =>
    caught !== null ? (
      <ResultFailure action={action} error={errorMessage(caught)} errorCode={failureCode(caught)} />
    ) : accepted ? (
      <ResultAccepted action={action} text={t('moduleCtl.done')} />
    ) : null

  const forgetLabel = t('action.forgetGrips')
  const resetLabel = t('action.resetRecoveryLock')
  const diagLabel = t('action.runDiagnostics')
  const diagResult: ReactNode = diagnostics.isPending ? (
    <p className="small muted" role="status">
      {`${diagLabel} — ${t('reason.requestInFlight')}`}
    </p>
  ) : diagnostics.error !== null ? (
    <ResultFailure
      action={diagLabel}
      error={errorMessage(diagnostics.error)}
      errorCode={failureCode(diagnostics.error)}
    />
  ) : diagnostics.data === undefined ? null : (
    <DiagnosticsResult report={diagnostics.data} />
  )

  /*
   * 🔴 카드 머리 오른쪽 — 제어락. 쥐고 있으면 「내가 보유」 칩, 아니면 안내 한 줄(획득 단추는 작업 줄에 이미 있다 — 두 벌 만들지 않는다).
   *    값은 `useScreenAccess().lockHeld`(작업 줄과 같은 출처)다. 권한이 없는 사람에게도 안내는 같다 — 타일 사유가 권한을 말한다
   */
  const lock =
    access.lockHeld === true ? (
      <StatusBadge label={t('lock.heldByMe')} status="IDLE" tone="ok" />
    ) : (
      <span className="muted modctl__lock">{t('reason.lockRequired')}</span>
    )

  return (
    <ModulePrepareContainer>
      {(prepare) => {
        const results = (
          [
            ['forget', outcome(forgetLabel, forget.error, forget.isSuccess)],
            ['reset', outcome(resetLabel, resetRecovery.error, resetRecovery.isSuccess)],
            ['diagnostics', diagResult],
            ['prepare', prepare.result],
          ] as const
        ).filter(([, node]) => node !== null)
        return (
          <>
            <ModuleInitializeCard
              accepted={initialize.isSuccess}
              actions={
                <>
                  <ReasonActionButton
                    actionLabel={forgetLabel}
                    confirmBody={t('moduleCtl.forgetGrips.confirmBody', { module: moduleName })}
                    description={t('moduleCtl.desc.forgetGrips')}
                    gate={writeGate(FORGET_GRIPS_OPERATIONS, forget.isPending)}
                    icon="trash"
                    onSend={(reason) => forget.mutate({ moduleId: module.module_id, reason })}
                    sending={forget.isPending}
                  />
                  <ReasonActionButton
                    actionLabel={resetLabel}
                    confirmBody={t('moduleCtl.resetRecovery.confirmBody', { module: moduleName })}
                    description={t('moduleCtl.desc.resetRecovery')}
                    gate={writeGate(RESET_RECOVERY_OPERATIONS, resetRecovery.isPending)}
                    icon="shield"
                    onSend={(reason) =>
                      resetRecovery.mutate({ moduleId: module.module_id, reason })
                    }
                    sending={resetRecovery.isPending}
                  />
                  <ControlButton
                    description={t('moduleCtl.desc.diagnostics')}
                    gate={writeGate(DIAGNOSTICS_OPERATIONS, diagnostics.isPending)}
                    icon="check"
                    label={diagLabel}
                    onClick={() => diagnostics.mutate({ moduleId: module.module_id })}
                  />
                </>
              }
              control={control}
              extra={prepare.tile}
              lock={lock}
              moduleName={moduleName}
              onInitialize={() => {
                initialize.mutate({ moduleId: module.module_id })
              }}
              {...(results.length === 0
                ? {}
                : {
                    results: (
                      <>
                        {results.map(([key, node]) => (
                          <Fragment key={key}>{node}</Fragment>
                        ))}
                      </>
                    ),
                  })}
              occupiedPoints={occupiedPointsOf(module.handoff_occupancy).map(occupiedPointLabel)}
              status={
                <ModuleStatusChips stale={module.stale} status={moduleControlStatus(module)} />
              }
              {...(error === undefined
                ? {}
                : { error: errorMessage(error), errorCode: failureCode(error) })}
            />
            {prepare.dialog}
          </>
        )
      }}
    </ModulePrepareContainer>
  )
}

/** 실패 코드 원문 — `error_code`, 없으면 상태 코드(「초기화」 줄과 같다) */
function failureCode(error: ApiError): string | undefined {
  return error.error_code ?? (error.status === undefined ? undefined : String(error.status))
}
